import * as React from "react";
import { memo, useEffect, useRef } from "react";
import styled from "@mui/material/styles/styled";
import { PatientColorMap } from "../../layout/patientColorMap";
import { ReactComponent as CloseIcon } from "../../assets/closeDescriptionIcon.svg";
import { commonBoxShadowStyle } from "fe-common/ui/boxShadow";
import { ReactComponent as RoundInfoIcon } from "../../assets/infoIconSmall.svg";
import { usePatientFeatureFlags } from "../../hooks/usePatientFeatureFlags";
//region [[ Styles ]]

const DescriptionElementView = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "top",
  borderRadius: "4px",
  background: PatientColorMap.Unselected,
  color: PatientColorMap.Default,
  ...commonBoxShadowStyle,
  // transition: "all 0.5s ease-out, width 0.01s ease-out",
  "&.hide": {
    width: 0,
    height: 0,
    opacity: 0,
    overflow: "hidden",
    padding: 0,
    margin: 0,
  },
  "&.show": {
    width: "100%",
    opacity: 1,
    height: "fit-content",
    padding: "15px",
    marginTop: "10px",
  },
}));

const QuestionIcon = styled(RoundInfoIcon)({
  margin: "0 14px",
  "& circle": {
    stroke: PatientColorMap.Primary,
  },
  "& path": {
    fill: PatientColorMap.Primary,
  },
});

const DescriptionTextContainer = styled("div")({
  fontSize: "16px",
  marginBottom: "10px",
  fontWeight: 500,
  width: "100%",
  lineHeight: "24px",
});

const ButtonArea = styled("div")({
  display: "flex",
  cursor: "pointer",
  justifyContent: "flex-end",
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface DescriptionElementProps {
  descriptionText: string;
  onCloseDescription?: (event) => void;
  show: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const DescriptionText = memo(
  ({ show, ...props }: DescriptionElementProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollToLastAnswer = usePatientFeatureFlags("scrollToLastAnswerOnly");

    useEffect(() => {
      if (!scrollToLastAnswer) {
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 300);
      }
    }, [show, scrollToLastAnswer]);

    return (
      <DescriptionElementView
        className={show ? "show" : "hide"}
        ref={containerRef}
      >
        <QuestionIcon />
        <DescriptionTextContainer>
          {props.descriptionText}
        </DescriptionTextContainer>
        {props.onCloseDescription && (
          <ButtonArea>
            <CloseIcon onClick={props.onCloseDescription} />
          </ButtonArea>
        )}
      </DescriptionElementView>
    );
  },
);
